/**
 * Swipe Back Container Component
 *
 * Provides iOS-style swipe-from-edge gesture for back navigation
 * with visual feedback (shadow and indicator).
 *
 * @module SwipeBackContainer
 */

import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { useSwipeBack } from '@/hooks/useSwipeBack';

interface SwipeBackContainerProps {
  children: React.ReactNode;
  /** Enable/disable the gesture (default: true) */
  enabled?: boolean;
  /** Routes that should not support back navigation */
  excludedRoutes?: string[];
  /** Additional class name for the container */
  className?: string;
}

/**
 * Container component that enables swipe-to-go-back gesture
 */
export function SwipeBackContainer({
  children,
  enabled = true,
  excludedRoutes,
  className = '',
}: SwipeBackContainerProps) {
  const {
    isSwiping,
    swipeProgress,
    handlers,
    swipeStyle,
    canGoBack,
  } = useSwipeBack({
    enabled,
    excludedRoutes,
  });

  // Only show indicator when swiping
  const showIndicator = isSwiping && canGoBack;

  return (
    <div
      className={`relative w-full h-full overflow-hidden ${className}`}
      {...handlers}
    >
      {/* Back Indicator - Shows during swipe */}
      {showIndicator && (
        <div
          className="absolute left-0 top-0 bottom-0 z-50 flex items-center pointer-events-none"
          style={{
            opacity: Math.min(swipeProgress * 2, 1),
            transform: `translateX(${swipeProgress * 40 - 40}px)`,
          }}
        >
          <div
            className="w-10 h-10 ml-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center border border-gray-200/50"
            style={{
              transform: `scale(${0.5 + swipeProgress * 0.5})`,
            }}
          >
            <ChevronLeft className="w-6 h-6 text-gray-700" />
          </div>
        </div>
      )}

      {/* Shadow overlay during swipe */}
      {showIndicator && (
        <div
          className="absolute inset-0 z-40 pointer-events-none bg-gradient-to-r from-black/5 to-transparent"
          style={{
            opacity: swipeProgress,
          }}
        />
      )}

      {/* Content wrapper with swipe transform */}
      <div
        className="w-full h-full"
        style={swipeStyle}
      >
        {children}
      </div>

      {/* Edge indicator when not swiping - subtle hint */}
      {canGoBack && !isSwiping && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-16 bg-gray-300/30 rounded-r-full z-40 pointer-events-none" />
      )}
    </div>
  );
}

export default SwipeBackContainer;
